import { api, type Credentials } from '@/shared/api/client';
import { env } from '@/shared/config/env';

/**
 * Autenticação: OTP de 6 dígitos enviado por e-mail.
 *
 * `skipAuth` nas duas primeiras porque elas **são** a autenticação: acontecem antes
 * de existir sessão, e mandar credencial inexistente só disparia o interceptor de
 * 401 no meio do login. Todas as demais rotas da API exigem sessão.
 */

/**
 * Solicita o envio do código de acesso. Rejeita com `ApiError('notFound')` se o
 * e-mail não for de um operador.
 *
 * Rota do módulo parametrizado (`/approval/tokens`): o assunto e o HTML do e-mail vêm
 * da parametrização do evento, e por isso o evento vai no corpo. A legada
 * (`POST /tokens`) tem o texto fixo no código da API.
 */
export async function requestAccessToken(email: string): Promise<void> {
  await api.post('/approval/tokens', { miceEventId: env.miceEventId, email: email.trim() }, { skipAuth: true });
}

/**
 * Valida o token. Rejeita com `ApiError('validation')` quando o código está errado ou expirou.
 *
 * Rota do módulo (`/approval/tokens/verify`), e não a legada: o evento vai no corpo
 * porque o acesso é por par (evento, e-mail) — quem tem acesso a um painel não tem
 * automaticamente aos outros. Um e-mail que perdeu o acesso entre pedir e confirmar
 * o código recebe `notFound` aqui, mesmo com um código válido em mãos.
 *
 * Devolve o nome do operador, que a API manda no 200 (`{ nome }`) — é o único ponto
 * do login em que ele chega, e é o que o cabeçalho do painel mostra. Volta `null`
 * quando o cadastro não tem nome: quem chama tem reserva.
 */
export async function verifyAccessToken(email: string, token: string): Promise<string | null> {
  const response = await api.post<{ nome?: string } | null>(
    '/approval/tokens/verify',
    { miceEventId: env.miceEventId, email: email.trim(), token },
    { skipAuth: true },
  );

  return response.data?.nome?.trim() || null;
}

/**
 * Encerra a sessão no servidor (remove a chave do Redis).
 *
 * Rota do módulo (`/approval/signout`): a legada confirma o e-mail em `TB_Usuarios`
 * antes de apagar a chave, e o operador do painel não está mais lá — o que fazia o
 * logout responder 400 e a chave sobreviver até o TTL.
 *
 * A rota exige sessão, mas quem chama já descartou a local — e o interceptor do
 * axios roda em microtask, depois disso. Por isso a credencial vem no argumento e
 * vai em headers explícitos, com `skipAuth` para deixar claro que não é o
 * interceptor que a fornece aqui.
 *
 * Falhas são ignoradas pelo chamador: a sessão local é descartada de qualquer forma.
 */
export async function revokeSession({ email, token }: Credentials): Promise<void> {
  await api.post('/approval/signout', null, { skipAuth: true, headers: { email, token } });
}
